/** A project's dates, and the record of how they have moved.
 *
 * The point of this component is the second half. Editing a date is ordinary; showing every
 * time the finish date moved, with the reason given at the time, is what makes slip visible.
 * Each individual move usually looks reasonable — it is the fourth one, read next to the
 * first three, that tells you something.
 *
 * Moving an existing target date asks for a reason and the API refuses without one, so the
 * box here is not a nicety that a determined person can route around: any client that skips
 * it gets a 422.
 */

import { useState } from 'react'
import { CalendarClock, Pencil } from 'lucide-react'

import { Button, Can, DateInput, ErrorState, Field, Loading, Pill, inputClass } from '@/components/ui'
import { formatDate } from '@/lib/domain'
import type { Project } from '@/lib/domain'
import { Permissions } from '@/lib/session'
import { useDateChanges, useUpdateProject } from '@/features/projects/useProjects'

/** A movement, phrased the way somebody reading it would say it out loud. */
function movementLabel(days: number | null): string {
  if (days === null) return 'set'
  if (days === 0) return 'no change'
  return days > 0 ? `${days} days later` : `${Math.abs(days)} days earlier`
}

export function ProjectSchedule({ project }: { project: Project }) {
  const update = useUpdateProject(project.id)
  const { data, isPending } = useDateChanges(project.id)
  const [editing, setEditing] = useState(false)
  const [start, setStart] = useState(project.startDate ?? '')
  const [target, setTarget] = useState(project.targetDate ?? '')
  const [reason, setReason] = useState('')

  // A reason is required only when a target that already exists is being moved. Asking for
  // one when the date is being set for the first time would make every new project justify a
  // commitment nobody had made yet.
  const targetMoved = Boolean(project.targetDate) && target !== (project.targetDate ?? '')
  const canSave = !targetMoved || reason.trim().length > 0

  const save = () => {
    update.mutate(
      {
        start_date: start || null,
        target_date: target || null,
        reason: reason.trim(),
        row_version: project.rowVersion,
      },
      {
        onSuccess: () => {
          setReason('')
          setEditing(false)
        },
      },
    )
  }

  const changes = data?.changes ?? []

  return (
    <section className="mt-6">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <CalendarClock size={15} aria-hidden style={{ color: 'var(--brand)' }} />
        <span className="micro-label text-fg-muted">Schedule</span>
        <span className="flex-1" />
        <Can permission={Permissions.projectsEdit}>
          {!editing ? (
            <Button
              variant="secondary"
              onClick={() => {
                setStart(project.startDate ?? '')
                setTarget(project.targetDate ?? '')
                setReason('')
                setEditing(true)
              }}
            >
              <Pencil size={14} aria-hidden />
              Change dates
            </Button>
          ) : null}
        </Can>
      </div>

      {editing ? (
        <div className="card mb-3 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Start">
              <DateInput value={start} onChange={setStart} />
            </Field>
            <Field label="Target">
              <DateInput value={target} onChange={setTarget} />
            </Field>
          </div>

          {targetMoved ? (
            <div className="mt-3">
              <Field
                label="Why is the target moving?"
                hint="Recorded against this project for good. Everyone sees it."
              >
                <input
                  className={inputClass}
                  value={reason}
                  autoFocus
                  placeholder="e.g. vendor slipped the hardware delivery"
                  onChange={(event) => setReason(event.target.value)}
                />
              </Field>
            </div>
          ) : null}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button variant="primary" disabled={!canSave || update.isPending} onClick={save}>
              {update.isPending ? 'Saving…' : 'Save dates'}
            </Button>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            {targetMoved && !canSave ? (
              <span className="text-xs text-fg-muted">
                A moved target needs a reason before it can be saved.
              </span>
            ) : null}
          </div>

          {update.error ? (
            <div className="mt-2">
              <ErrorState error={update.error} />
            </div>
          ) : null}
        </div>
      ) : (
        <dl className="card mb-3 grid grid-cols-2 gap-3 p-4 text-sm">
          <div>
            <dt className="micro-label text-fg-muted">Start</dt>
            <dd className="text-fg">{formatDate(project.startDate)}</dd>
          </div>
          <div>
            <dt className="micro-label text-fg-muted">Target</dt>
            <dd className="text-fg">{formatDate(project.targetDate)}</dd>
          </div>
        </dl>
      )}

      {isPending ? (
        <Loading label="Reading the date history" />
      ) : changes.length === 0 ? (
        // Not an empty state to apologise for. Most projects have never moved a date, and
        // saying so plainly is the good outcome.
        <p className="text-xs text-fg-muted">These dates have not moved.</p>
      ) : (
        <ol className="card divide-y divide-border">
          {changes.map((change) => (
            <li key={change.id} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Pill tone={change.field === 'target_date' ? 'amber' : 'neutral'}>
                  {change.field === 'target_date' ? 'Target' : 'Start'}
                </Pill>
                <span className="text-fg">
                  {change.previousDate ? formatDate(change.previousDate) : '—'} →{' '}
                  {formatDate(change.newDate)}
                </span>
                <span className="text-xs text-fg-muted">{movementLabel(change.daysMoved)}</span>
              </div>
              {change.reason ? (
                <p className="mt-1 text-sm text-fg-muted">{change.reason}</p>
              ) : null}
              <p className="mt-1 text-[11px] text-fg-muted">
                {change.changedBy} · {new Date(change.changedAt).toLocaleString()}
              </p>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}
